import { Employee, EmployeeMapData, EmployeeWeeklyRecord, WeekData } from './types';

export const calculateEmployeeData = (task: {
  empUserId: string;
  name: string;
  income?: number;
  supplies?: number;
  credits?: number;
}): {
  taskIncome: number;
  taskSupplies: number;
  taskCredits: number;
} => {
  return {
    taskIncome: task.income || 0,
    taskSupplies: task.supplies || 0,
    taskCredits: task.credits || 0,
  };
};

export const calculateFinalAmounts = (
  emp: EmployeeMapData,
  totalIncome: number
): EmployeeWeeklyRecord => {
  const percentage = totalIncome === 0 ? 0 : (emp.income / totalIncome) * 100;
  const income_final = emp.income - emp.supplies;
  const salaryAmount = income_final > 0 ? Math.round((income_final * emp.commission) / 100) : 0;
  const checkAmount = emp.checkPercent ? Math.round((salaryAmount * emp.checkPercent) / 100) : 0;
  const tipAmount = checkAmount > 0 ? Math.round(checkAmount * 0.05) : 0;
  const cashAmount = salaryAmount - (checkAmount + tipAmount);

  return {
    name: emp.name,
    income: emp.income,
    supplies: emp.supplies,
    totalTasks: emp.totalTasks,
    percentage,
    salary: salaryAmount,
    check: {
      amount: checkAmount,
      tip: tipAmount,
      cash: cashAmount,
    },
  };
};

export const processWeeklyData = (
  weekData: WeekData,
  employees: Employee[]
): EmployeeWeeklyRecord[] => {
  const employeeMap = new Map<string, EmployeeMapData>();

  // Aggregate data by employee from daily records
  weekData.records.forEach(dayData => {
    dayData.tasks.forEach(task => {
      if (!task.empUserId) return;

      const employee = employees.find(emp => emp.empUserId === task.empUserId);
      if (!employeeMap.has(task.empUserId)) {
        employeeMap.set(task.empUserId, {
          name: task.name,
          income: 0,
          supplies: 0,
          totalTasks: 0,
          totalAmount: 0,
          commission: employee?.empCommission || 50, // Default to 50% if not found
          checkPercent: employee?.empCheckPercent || 60, // Default to 60% if not found
          tasks: [], // Initialize tasks array
        });
      }

      const emp = employeeMap.get(task.empUserId)!;
      const { taskIncome, taskSupplies, taskCredits } = calculateEmployeeData(task);

      emp.income += taskIncome;
      emp.supplies += taskSupplies;
      emp.totalTasks += taskCredits;
      emp.totalAmount += emp.income;
      emp.tasks.push(task); // Add task to employee's tasks array
    });
  });

  // Calculate total income for percentage calculation
  const totalIncome = Array.from(employeeMap.values()).reduce((sum, emp) => sum + emp.income, 0);

  return Array.from(employeeMap.values()).map(emp => ({
    ...calculateFinalAmounts(emp, totalIncome),
    tasks: emp.tasks, // Include tasks in the final record
  }));
};
